/**
 * Hook to handle clicks on highlighted text
 * Opens the corresponding item in a new tab and navigates to it
 */

import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSelectedGame } from '@/lib/stores/unifiedUserStore';

export const useHighlightClickHandler = () => {
  const navigate = useNavigate();

  const handleHighlightClick = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement;
    
    // Check if the clicked element is a highlight or inside one
    const highlightElement = target.classList.contains('text-highlight') 
      ? target 
      : target.closest('.text-highlight');

    if (!highlightElement) return;

    // Get highlight data
    const highlightId = highlightElement.getAttribute('data-highlight-id');
    const highlightType = highlightElement.getAttribute('data-highlight-type');
    const highlightName = highlightElement.getAttribute('data-highlight-name');

    if (!highlightId || !highlightType) return;

    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    // Get selected game
    const selectedGame = getSelectedGame();
    if (!selectedGame?.id) return;

    // Open item based on type and navigate to it - tab system handles tab creation
    if (highlightType === 'glossary') {
      const path = `/library/${highlightId}`;
      navigate(path);
    } else if (highlightType === 'document') {
      const path = `/documents/${highlightId}`;
      navigate(path);
    } else if (highlightType === 'blueprint') {
      const path = `/templates/${highlightId}`;
      navigate(path);
    }
    // Add more item types as needed
  }, [navigate]);

  useEffect(() => {
    // Add click listener to document
    document.addEventListener('click', handleHighlightClick);

    return () => {
      document.removeEventListener('click', handleHighlightClick);
    };
  }, [handleHighlightClick]);

  return { handleHighlightClick };
};

